/* STEP: Entry — choose new account code (existing parent) OR new parent.
   Parent list comes from the backend (useLazyListParentsQuery): ACTIVE parents are
   selectable for adding codes; DRAFT parents resume onboarding (jump to the
   Contacts step with their data prefetched via getParent).

   Styled to match the rest of the admin panel (same option-card pattern as
   d2c-pricing-agent: plain bordered Box, border/background-tint selection state,
   no elevation glow, no icon tiles, dense ~12-13px typography) instead of the
   earlier Merchant-mockup OptionCards. All wizard logic (parentMode, draft-resume,
   validation, step transitions) is unchanged — this is a presentational change only. */
import React, {useEffect, useState, useCallback, useRef} from 'react'
import PropTypes from 'prop-types'
import {alpha} from '@mui/material/styles'
import {Box, Stack, Typography, Autocomplete, TextField, Button, Chip, CircularProgress} from '@mui/material'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import AddBusinessOutlinedIcon from '@mui/icons-material/AddBusinessOutlined'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CheckIcon from '@mui/icons-material/Check'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import {useToast, initials} from '../../components'
import {apiError, INTEGRATION_LABEL, POC_ROLE_LABEL} from '../../supplyMeta'
import {useLazyListParentsQuery, useLazyGetParentQuery} from '../../../../redux/supplyApiSlice'

const OPTS = [
  {
    id: 'existing',
    title: 'Add to an existing parent',
    sub: 'Pick a parent already in the directory (e.g. Delhivery, Shadowfax) and set up a new account code under it.',
    Icon: AccountTreeOutlinedIcon,
  },
  {
    id: 'new',
    title: 'Set up a new parent',
    sub: 'Create a new parent courier first (details, contacts, COD SLA), then its first account code.',
    Icon: AddBusinessOutlinedIcon,
  },
]

/* backend parent document → wizard A slice + pocs */
function parentToA(par) {
  return {
    name: par.name || '',
    legal: par.legalEntityName || '',
    verticals: par.verticals && par.verticals.length ? par.verticals : ['D2C'],
    gst: par.gstNumber || '',
    integration: INTEGRATION_LABEL[par.integrationMethod] || '',
    website: par.website || '',
    address: par.registeredAddress || '',
  }
}
function contactToPoc(c) {
  return {
    name: c.name || '',
    desig: c.designation || '',
    roles: (c.roles || []).map((r) => POC_ROLE_LABEL[r]).filter(Boolean),
    email: c.email || '',
    m1: c.mobilePrimary || '',
    m2: c.mobileSecondary || '',
    linkedin: c.linkedinUrl || '',
    notes: c.notes || '',
  }
}

const parentLabel = (p) =>
  !p ? '' : p.name + (p.status === 'DRAFT' ? ' (draft — finish contacts)' : p.status === 'INACTIVE' ? ' (inactive)' : '')

const STATUS_TONE = {
  ACTIVE: {label: 'Active', fg: '#166534', bg: '#DCFCE7'},
  DRAFT: {label: 'Draft', fg: '#92400E', bg: '#FEF3C7'},
  INACTIVE: {label: 'Inactive', fg: '#475569', bg: '#F1F5F9'},
}

export default function StepEntry({W, dispatch, resumeParentId}) {
  const toast = useToast()
  const [parentInvalid, setParentInvalid] = useState(false)
  const [parents, setParents] = useState([])
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [selId, setSelId] = useState('')
  const [fetchParents] = useLazyListParentsQuery()
  const [fetchParent] = useLazyGetParentQuery()
  const autoResumed = useRef(false)

  /* resume onboarding for a DRAFT parent — load it and jump into the Contacts step.
     Hard guard: only ever resumes a genuine DRAFT. Without this, a stale/contaminated
     parentRef (e.g. from a bad deep-link) could get treated as an in-progress new-parent
     draft, and StepDetails.js's save-as-update would silently overwrite a real ACTIVE
     parent's details with whatever gets typed/prefilled next. */
  const resumeDraft = useCallback(
    async (p) => {
      if (p.status !== 'DRAFT') {
        toast((p.name || 'This parent') + ' is not a draft — it already exists in the directory. Pick "Add to an existing parent" instead.', 'error')
        return
      }
      setBusy(true)
      try {
        const par = (await fetchParent(p.id).unwrap()) || p
        if (par.status !== 'DRAFT') {
          toast((par.name || p.name) + ' is not a draft — refusing to resume onboarding for it.', 'error')
          return
        }
        dispatch({type: 'SET', slice: 'parentMode', value: 'new'})
        dispatch({type: 'SET', slice: 'parentRef', value: {id: par.id || p.id, name: par.name || p.name, integrationMethod: par.integrationMethod, status: par.status}})
        dispatch({type: 'PATCH', slice: 'A', value: parentToA(par)})
        if (par.contacts && par.contacts.length) dispatch({type: 'SET', slice: 'pocs', value: par.contacts.map(contactToPoc)})
        dispatch({type: 'STEP', step: 2}) // contacts step in the new-parent plan
        toast('Resuming onboarding for ' + (par.name || p.name) + ' — finish the contacts to activate it.', 'success')
      } catch (e) {
        toast(apiError(e), 'error')
      } finally {
        setBusy(false)
      }
    },
    [fetchParent, dispatch, toast]
  )

  useEffect(() => {
    let alive = true
    setLoading(true)
    fetchParents()
      .unwrap()
      .then((list) => {
        if (!alive) return
        setParents(Array.isArray(list) ? list : (list && list.data) || [])
      })
      .catch((e) => alive && toast('Could not load parents: ' + apiError(e), 'error'))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // deep-link resume: when ?resume=<parentId> matches a loaded DRAFT parent, jump straight in.
  // Only DRAFT parents are eligible — an ACTIVE/INACTIVE match (e.g. a stale bookmark) is
  // left alone so it can't be silently treated as an in-progress new-parent draft.
  useEffect(() => {
    if (!resumeParentId || autoResumed.current || !parents.length) return
    const p = parents.find((x) => String(x.id) === String(resumeParentId))
    if (p && p.status === 'DRAFT') {
      autoResumed.current = true
      setSelId(String(p.id))
      resumeDraft(p)
    }
  }, [resumeParentId, parents, resumeDraft])

  const selectMode = (id) => dispatch({type: 'SET', slice: 'parentMode', value: id})

  const onContinue = async () => {
    if (W.parentMode === 'new') {
      dispatch({type: 'STEP', step: 1})
      return
    }
    const p = parents.find((x) => String(x.id) === selId)
    if (!p) {
      setParentInvalid(true)
      toast('Select a parent courier.', 'error')
      return
    }
    if (p.status === 'DRAFT') {
      await resumeDraft(p)
      return
    }
    dispatch({type: 'SET', slice: 'parentRef', value: {id: p.id, name: p.name, integrationMethod: p.integrationMethod, status: p.status}})
    dispatch({type: 'FIELD', slice: 'D', key: 'parentId', value: p.name})
    dispatch({type: 'STEP', step: 1})
  }

  const selectedParent = parents.find((x) => String(x.id) === selId) || null

  return (
    <Box sx={{width: '100%'}}>
      <Box
        sx={{
          borderRadius: '10px',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 1px 3px rgba(0,0,0,.06)',
          bgcolor: 'background.paper',
          p: {xs: 2, md: 2.25},
        }}
      >
        {/* heading */}
        <Box sx={{mb: 2}}>
        <Typography sx={{fontWeight: 700, fontSize: 15, color: 'text.primary'}}>Where should this account code live?</Typography>
        <Typography sx={{fontSize: 12, color: 'text.secondary', mt: 0.5}}>
          Choose whether this code sits under a courier you already onboarded, or a brand-new one.
        </Typography>
      </Box>

      {/* option tiles — side by side, fill the width */}
      <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'}, gap: 1.5}}>
        {OPTS.map((o) => {
          const checked = W.parentMode === o.id
          return (
            <Box
              key={o.id}
              onClick={() => selectMode(o.id)}
              sx={{
                cursor: 'pointer',
                borderRadius: '10px',
                border: '1.5px solid',
                borderColor: checked ? 'primary.main' : 'divider',
                bgcolor: checked ? (t) => alpha(t.palette.primary.main, 0.06) : 'background.paper',
                p: 1.5,
                transition: 'border-color .15s, background-color .15s',
                '&:hover': {borderColor: 'primary.main'},
              }}
            >
              <Stack direction='row' alignItems='flex-start' spacing={1}>
                <o.Icon sx={{fontSize: 18, color: checked ? 'primary.main' : 'text.secondary', mt: 0.25}} />
                <Box sx={{flex: 1, minWidth: 0}}>
                  <Stack direction='row' alignItems='center' justifyContent='space-between'>
                    <Typography sx={{fontWeight: 700, fontSize: 13, color: checked ? 'primary.dark' : 'text.primary'}}>
                      {o.title}
                    </Typography>
                    {checked ? <CheckIcon sx={{color: 'primary.main', fontSize: 16}} /> : null}
                  </Stack>
                  <Typography sx={{fontSize: 12, color: 'text.secondary', mt: 0.25, lineHeight: 1.5}}>{o.sub}</Typography>
                </Box>
              </Stack>
            </Box>
          )
        })}
      </Box>

      {/* existing-parent picker */}
      {W.parentMode === 'existing' ? (
        <Box sx={{mt: 3, maxWidth: 520}}>
          <Autocomplete
            options={parents}
            value={selectedParent}
            loading={loading}
            getOptionLabel={parentLabel}
            getOptionDisabled={(p) => p.status !== 'ACTIVE' && p.status !== 'DRAFT'}
            isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
            onChange={(_e, val) => {
              setSelId(val ? String(val.id) : '')
              setParentInvalid(false)
            }}
            renderOption={(props, p) => {
              const {key, ...rest} = props
              const tone = STATUS_TONE[p.status] || {label: p.status, fg: '#475569', bg: '#F1F5F9'}
              return (
                <Box component='li' key={p.id} {...rest} sx={{gap: 1.25, py: 0.75}}>
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: '8px',
                      flexShrink: 0,
                      bgcolor: 'primary.main',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                  >
                    {initials(p.name)}
                  </Box>
                  <Box sx={{flex: 1, minWidth: 0}}>
                    <Typography noWrap sx={{fontSize: 13.5, fontWeight: 600, color: 'text.primary'}}>
                      {p.name}
                    </Typography>
                    {p.legalEntityName ? (
                      <Typography noWrap sx={{fontSize: 11.5, color: 'text.secondary'}}>
                        {p.legalEntityName}
                      </Typography>
                    ) : null}
                  </Box>
                  <Chip
                    label={tone.label}
                    size='small'
                    sx={{height: 20, fontSize: 10, fontWeight: 700, bgcolor: tone.bg, color: tone.fg, '& .MuiChip-label': {px: 0.9}}}
                  />
                </Box>
              )
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                size='small'
                label='Parent courier'
                required
                error={parentInvalid}
                placeholder={loading ? 'Loading parents…' : 'Search or select a parent…'}
                helperText={parentInvalid ? 'Select a parent.' : ''}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: selectedParent ? (
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '6px',
                        ml: 0.5,
                        mr: 0.25,
                        flexShrink: 0,
                        bgcolor: 'primary.main',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 10,
                        fontWeight: 700,
                      }}
                    >
                      {initials(selectedParent.name)}
                    </Box>
                  ) : (
                    <StorefrontOutlinedIcon sx={{fontSize: 18, color: 'text.disabled', ml: 0.75, mr: 0.25}} />
                  ),
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{'& .MuiOutlinedInput-root': {borderRadius: '8px', bgcolor: 'background.paper'}}}
          />
          <Stack direction='row' spacing={0.5} alignItems='center' sx={{mt: 0.75, color: 'text.secondary'}}>
            <InfoOutlinedIcon sx={{fontSize: 14}} />
            <Typography sx={{fontSize: 12}}>
              ACTIVE parents take a new account code; selecting a draft resumes its onboarding.
            </Typography>
          </Stack>
        </Box>
      ) : null}

      {/* footer */}
      <Stack direction='row' justifyContent='flex-end' sx={{mt: 2.5}}>
        <Button
          variant='contained'
          disabled={!W.parentMode || busy}
          onClick={onContinue}
          endIcon={busy ? <CircularProgress size={16} color='inherit' /> : <ArrowForwardIcon />}
          sx={{borderRadius: '8px', textTransform: 'none', px: 2.5, py: 0.9, fontWeight: 700, fontSize: 13.5}}
        >
          {busy ? 'Loading…' : 'Continue'}
        </Button>
      </Stack>
      </Box>
    </Box>
  )
}

StepEntry.propTypes = {
  W: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  resumeParentId: PropTypes.string,
}
